import React, { useEffect, useState } from "react";
import { submitUser, loadUserForm } from "../../api/adminApi";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "react-toastify/dist/ReactToastify.css";

const NewUser = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
  });

  const [errors, setErrors] = useState({});
  const [roleList, setRoleList] = useState([]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await loadUserForm();
        if (res.status === 200) {
          const data = res.data;
          setRoleList(data.roleList.filter(r => r === "USER" || r === "MODERATOR"));
        }
      } catch (err) {
        toast.error("Failed to load user form");
      }
    };
    fetchForm();
  }, []);

  const validate = () => {
  const newErrors = {};

  // Name: only letters, 2–50 characters
  if (!form.name.trim()) {
    newErrors.name = "Name is required.";
  } else if (!/^[A-Za-z\s]{2,50}$/.test(form.name.trim())) {
    newErrors.name = "Name must be 2–50 letters only.";
  }

  // Username: 5–25, alphanumeric
  if (!form.username.trim()) {
    newErrors.username = "Username is required.";
  } else if (!/^[A-Za-z0-9]{5,25}$/.test(form.username.trim())) {
    newErrors.username = "Username must be 5–25 characters, letters & numbers only.";
  }

  // Email
  if (!form.email.trim()) {
    newErrors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    newErrors.email = "Enter a valid email address.";
  }

  // Role
  if (!form.role) {
    newErrors.role = "Role is required.";
  }

  // Password
  if (!form.password) {
    newErrors.password = "Password is required.";
  } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,50}$/.test(form.password)) {
    newErrors.password =
      "Password must be 8–50 chars, include uppercase, lowercase, and number.";
  }

  // Confirm password
  if (form.password !== form.confirmPassword) {
    newErrors.confirmPassword = "Passwords do not match.";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirmPassword;

      const response = await submitUser(payload);

      if (response.status === 201 || response.status === 200) {
        toast.success("User created successfully!");
        setTimeout(() => navigate("/admin/manage-user"), 2000);
      } else {
        toast.error("Failed to create user.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-blue-100">
      <ToastContainer position="top-right" />
      <form
        onSubmit={handleSubmit}
        className="bg-white p-5 rounded-lg shadow-lg w-full max-w-lg border-t-4 border-green-400"
      >
        <h2 className="text-3xl font-extrabold mb-6 text-blue-800 text-center">
          Create New User
        </h2>

        {[
          { label: "Full Name", name: "name", type: "text" },
          { label: "Username", name: "username", type: "text" },
          { label: "Email", name: "email", type: "email" },
        ].map(({ label, name, type }) => (
          <div className="mb-4" key={name}>
            <label className="block text-blue-800 mb-2 font-medium" htmlFor={name}>
              {label}
            </label>
            <input
              id={name}
              name={name}
              type={type}
              className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${errors[name]
                ? "border-red-500 focus:ring-red-300"
                : "border-gray-300 focus:ring-blue-300"
                }`}
              value={form[name]}
              onChange={handleChange}
            />
            {errors[name] && <p className="text-red-600 text-sm mt-1">{errors[name]}</p>}
          </div>
        ))}

        {/* Password */}
        <div className="mb-4 relative">
          <label className="block text-blue-800 mb-2 font-medium" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${errors.password
              ? "border-red-500 focus:ring-red-300"
              : "border-gray-300 focus:ring-blue-300"
              }`}
            value={form.password}
            onChange={handleChange}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-blue-500 hover:text-green-600"
            onClick={() => setShowPassword(!showPassword)}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </button>
          {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
        </div>

        {/* Confirm Password */}
        <div className="mb-4 relative">
          <label className="block text-blue-800 mb-2 font-medium" htmlFor="confirmPassword">
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirm ? "text" : "password"}
            className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${errors.confirmPassword
              ? "border-red-500 focus:ring-red-300"
              : "border-gray-300 focus:ring-blue-300"
              }`}
            value={form.confirmPassword}
            onChange={handleChange}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-blue-500 hover:text-green-600"
            onClick={() => setShowConfirm(!showConfirm)}
          >
            <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
          </button>
          {errors.confirmPassword && (
            <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        {/* Role */}
        <div className="mb-6">
          <label className="block text-blue-800 mb-2 font-medium" htmlFor="role">
            Role
          </label>
          <select
            id="role"
            name="role"
            className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${errors.role
              ? "border-red-500 focus:ring-red-300"
              : "border-gray-300 focus:ring-blue-300"
              }`}
            value={form.role}
            onChange={handleChange}
          >
            <option value="">Select role</option>
            {roleList.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          {errors.role && <p className="text-red-600 text-sm mt-1">{errors.role}</p>}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-500 text-white font-semibold py-3 rounded hover:bg-green-600 transition disabled:opacity-50"
        >
          {loading ? "Creating..." : "Create User"}
        </button>
      </form>
    </div>
  );
};

export default NewUser;
